import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';

export interface Tijdslot {
  weekday: number;      // 1 = maandag … 7 = zondag
  start_time: string;   // 'HH:MM'
  end_time: string;     // 'HH:MM'
  slot_type?: 'intake' | 'sessie' | 'beide';
}

interface TijdslotAgendaProps {
  slots: Tijdslot[];
  onChange: (slots: Tijdslot[]) => void;
  /** Minimum uit het FO (stap 9); onder dit aantal is het profiel incompleet */
  minSlots?: number;
}

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

const DEFAULT_WEEKDAY_LABELS: Record<number, string> = {
  1: 'Maandag', 2: 'Dinsdag', 3: 'Woensdag', 4: 'Donderdag',
  5: 'Vrijdag', 6: 'Zaterdag', 7: 'Zondag',
};

/** 'HH:MM' → minuten sinds middernacht */
function tijdNaarMinuten(tijd: string): number {
  const [uur, minuut] = tijd.split(':').map(Number);
  return uur * 60 + minuut;
}

/** Uren tussen twee 'HH:MM'-tijden */
function slotHours(slot: Tijdslot): number {
  return (tijdNaarMinuten(slot.end_time) - tijdNaarMinuten(slot.start_time)) / 60;
}

/**
 * Wekelijkse beschikbaarheid met vrije tijden per dag.
 *
 * Geen vaste dagdelen: de professional kiest zelf per dag vanaf hoe laat hij
 * begint en tot hoe laat hij doorwerkt, met desgewenst meerdere blokken per dag
 * (bijv. 09:00–12:30 én 19:00–21:00). Maximale vrijheid.
 *
 * Wet DBA: er wordt nooit naar een vast aantal uren per week gevraagd — de
 * professional zet zelf blokken open en die bepalen de statusindicator.
 */
export default function TijdslotAgenda({ slots, onChange, minSlots = 3 }: TijdslotAgendaProps) {
  const { t } = useTranslation('profile');

  const weekdayLabel = (day: number) =>
    t(`tijdslot.weekday.${day}`, { defaultValue: DEFAULT_WEEKDAY_LABELS[day] });

  // Invoerstaat per weekdag (van/tot), zodat elke dag zijn eigen invulregel heeft
  const [invoer, setInvoer] = useState<Record<number, { van: string; tot: string }>>({});
  const [fout, setFout] = useState<Record<number, string | null>>({});

  const invoerVoor = (weekday: number) => invoer[weekday] ?? { van: '09:00', tot: '17:00' };

  const zetInvoer = (weekday: number, veld: 'van' | 'tot', waarde: string) => {
    setInvoer((huidig) => ({ ...huidig, [weekday]: { ...invoerVoor(weekday), [veld]: waarde } }));
    setFout((huidig) => ({ ...huidig, [weekday]: null }));
  };

  const voegToe = (weekday: number) => {
    const { van, tot } = invoerVoor(weekday);
    if (!van || !tot || tijdNaarMinuten(tot) <= tijdNaarMinuten(van)) {
      setFout((huidig) => ({
        ...huidig,
        [weekday]: t('tijdslot.ongeldigeTijd', { defaultValue: 'De eindtijd moet na de starttijd liggen.' }),
      }));
      return;
    }
    const overlapt = slots.some(
      (slot) =>
        slot.weekday === weekday &&
        tijdNaarMinuten(van) < tijdNaarMinuten(slot.end_time) &&
        tijdNaarMinuten(tot) > tijdNaarMinuten(slot.start_time)
    );
    if (overlapt) {
      setFout((huidig) => ({
        ...huidig,
        [weekday]: t('tijdslot.overlap', { defaultValue: 'Dit blok overlapt met een bestaand blok op deze dag.' }),
      }));
      return;
    }
    const nieuw: Tijdslot = { weekday, start_time: van, end_time: tot, slot_type: 'beide' };
    onChange(
      [...slots, nieuw].sort(
        (a, b) => a.weekday - b.weekday || tijdNaarMinuten(a.start_time) - tijdNaarMinuten(b.start_time)
      )
    );
  };

  const verwijder = (weekday: number, start: string) =>
    onChange(slots.filter((slot) => !(slot.weekday === weekday && slot.start_time === start)));

  const totalHours = slots.reduce((sum, slot) => sum + slotHours(slot), 0);
  // Zelfde drempel als availabilityIndicator() in de backend-service
  const indicator = slots.length === 0 ? 'geen' : totalHours >= 8 ? 'direct' : 'beperkt';
  const indicatorStyles: Record<string, string> = {
    direct: 'bg-green-50 text-green-800 border-green-200',
    beperkt: 'bg-amber-50 text-amber-800 border-amber-200',
    geen: 'bg-gray-50 text-gray-600 border-gray-200',
  };
  const indicatorDots: Record<string, string> = {
    direct: '🟢', beperkt: '🟡', geen: '⚪',
  };

  return (
    <div>
      <div className="space-y-2">
        {WEEKDAYS.map((weekday) => {
          const dagSlots = slots
            .filter((slot) => slot.weekday === weekday)
            .sort((a, b) => tijdNaarMinuten(a.start_time) - tijdNaarMinuten(b.start_time));
          const { van, tot } = invoerVoor(weekday);
          return (
            <div key={weekday} className="rounded-lg border border-gray-200 px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-24 shrink-0 text-sm font-medium text-gray-700">
                  {weekdayLabel(weekday)}
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  {dagSlots.length === 0 && (
                    <span className="text-xs text-gray-400">
                      {t('tijdslot.gesloten', { defaultValue: 'Niet beschikbaar' })}
                    </span>
                  )}
                  {dagSlots.map((slot) => (
                    <span
                      key={slot.start_time}
                      className="inline-flex items-center gap-1.5 rounded-md border border-green-500 bg-green-100 px-2 py-1 text-sm font-medium text-green-800"
                    >
                      {slot.start_time}–{slot.end_time}
                      <button
                        type="button"
                        onClick={() => verwijder(weekday, slot.start_time)}
                        aria-label={t('tijdslot.verwijderBlok', {
                          dag: weekdayLabel(weekday),
                          defaultValue: `Blok ${slot.start_time}–${slot.end_time} verwijderen`,
                        })}
                        className="text-green-700 hover:text-red-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="ml-auto flex items-center gap-1.5">
                  <input
                    type="time"
                    value={van}
                    onChange={(e) => zetInvoer(weekday, 'van', e.target.value)}
                    aria-label={t('tijdslot.van', { defaultValue: 'Van' })}
                    className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                  />
                  <span className="text-xs text-gray-400">–</span>
                  <input
                    type="time"
                    value={tot}
                    onChange={(e) => zetInvoer(weekday, 'tot', e.target.value)}
                    aria-label={t('tijdslot.tot', { defaultValue: 'Tot' })}
                    className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => voegToe(weekday)}
                    aria-label={t('tijdslot.toevoegen', {
                      dag: weekdayLabel(weekday),
                      defaultValue: 'Blok toevoegen',
                    })}
                    className="rounded-md border border-gray-300 p-1.5 text-gray-600 transition-colors hover:border-green-500 hover:text-green-700"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {fout[weekday] && <p className="mt-1 text-xs text-red-600">{fout[weekday]}</p>}
            </div>
          );
        })}
      </div>

      <div className={`mt-4 flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm ${indicatorStyles[indicator]}`}>
        <span>{indicatorDots[indicator]}</span>
        <span className="font-medium">
          {t(`tijdslot.indicator.${indicator}`, {
            defaultValue: indicator === 'direct'
              ? 'Direct beschikbaar'
              : indicator === 'beperkt'
                ? 'Beperkt beschikbaar'
                : 'Nog geen tijdslots open',
          })}
        </span>
        <span className="text-xs">
          {t('tijdslot.summary', {
            count: slots.length,
            hours: Math.round(totalHours * 10) / 10,
            defaultValue: `${slots.length} tijdslots · ${Math.round(totalHours * 10) / 10} uur per week`,
          })}
        </span>
      </div>

      {slots.length < minSlots && (
        <p className="mt-2 text-sm text-amber-700">
          {t('tijdslot.minimum', {
            count: minSlots,
            defaultValue: `Zet minimaal ${minSlots} tijdslots open om je profiel te kunnen publiceren.`,
          })}
        </p>
      )}
    </div>
  );
}
